import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Brain, BarChart3, TrendingUp, Users, DollarSign, Database } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useOpenAIService } from "@/hooks/useOpenAIService";
import { useNavigate } from "react-router-dom";

export const EnhancedDashboardAI = () => {
  const [insights, setInsights] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasData, setHasData] = useState<boolean | null>(null);
  const { callAIService } = useOpenAIService();
  const navigate = useNavigate();

  useEffect(() => {
    const checkData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { count } = await supabase
          .from("data_imports")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id);
        setHasData((count ?? 0) > 0);
      } catch {
        setHasData(false);
      }
    };
    checkData();
  }, []);

  const generateEnhancedInsights = async () => {
    setIsGenerating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { toast.error("Please log in"); return; }

      const { data: profile } = await supabase
        .from("profiles")
        .select("industry, business_size")
        .eq("id", user.id)
        .single();

      const { data: dataSources } = await supabase
        .from("data_sources")
        .select("*")
        .eq("user_id", user.id);

      await callAIService({
        feature: 'dashboard_insights',
        query: `Generate comprehensive business insights for a ${profile?.business_size} ${profile?.industry} company with ${dataSources?.length || 0} data sources.`,
        industry: profile?.industry || 'tech',
        userId: user.id,
        interactionType: 'dashboard_generation'
      });

      // Use real insights from proactive_insights after AI generates them
      const { data: realInsights } = await supabase
        .from("proactive_insights")
        .select("*")
        .eq("user_id", user.id)
        .eq("is_dismissed", false)
        .order("created_at", { ascending: false })
        .limit(3);

      if (realInsights && realInsights.length > 0) {
        setInsights(realInsights.map((i, idx) => ({
          id: i.id,
          title: i.title,
          description: i.description,
          type: i.severity === "high" ? "warning" : "positive",
          value: i.severity,
          icon: idx === 0 ? TrendingUp : idx === 1 ? Users : DollarSign,
          color: i.severity === "high" ? "text-red-600" : "text-green-600",
        })));
      }

      toast.success("Insights generated!");
    } catch (error: any) {
      console.error("Error generating insights:", error);
      toast.error("Failed to generate insights");
    } finally {
      setIsGenerating(false);
    }
  };

  // Not loaded yet
  if (hasData === null) return null;

  // No data — show CTA instead of auto-generating
  if (!hasData) {
    return (
      <Card className="p-6">
        <div className="flex flex-col items-center text-center py-4">
          <Database className="w-10 h-10 text-muted-foreground mb-3" />
          <h3 className="font-semibold text-lg mb-1">Connect Data to Unlock AI Insights</h3>
          <p className="text-sm text-muted-foreground max-w-md mb-4">
            Import your data or connect a source to get AI-powered business intelligence.
          </p>
          <Button onClick={() => navigate("/data")} variant="outline">
            Import Data
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Brain className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">AI-Powered Insights</h3>
          </div>
          <Button onClick={generateEnhancedInsights} disabled={isGenerating} variant="outline" size="sm">
            {isGenerating ? "Generating..." : "Generate Insights"}
          </Button>
        </div>

        {insights.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-4">
            {insights.map((insight) => (
              <div key={insight.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-2">
                  <insight.icon className={`w-5 h-5 ${insight.color}`} />
                  <span className={`text-lg font-semibold ${insight.color}`}>{insight.value}</span>
                </div>
                <h4 className="font-medium mb-1">{insight.title}</h4>
                <p className="text-sm text-muted-foreground">{insight.description}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              Click "Generate Insights" to get AI-powered analysis of your data
            </p>
          </div>
        )}
      </Card>

      {metrics.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <BarChart3 className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Key Performance Metrics</h3>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {metrics.map((metric, index) => (
              <div key={index} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-bold">{metric.value}</span>
                  <span className={`text-sm font-medium ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                    {metric.change}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{metric.label}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
